<script lang="ts">
	/**
	 * `/Settings/Admin/Reports` — what the store took (Design §13).
	 *
	 * **Beside Analytics rather than inside it, and the split is by source as much as by question.**
	 * Analytics reads the visit log and answers "what did people do"; this reads invoices, orders
	 * and refunds — the money's own tables — and answers "what did the store take". Folding them
	 * would put a funnel between a revenue chart and a VAT breakdown, and the page would be about
	 * neither. It is also read by different people: analytics is a marketing question, this is the
	 * one an accountant, a board pack and a tax return are all asking.
	 *
	 * **One filter bar drives every panel**, reflected into the URL so a slice can be sent to
	 * somebody. Each panel then fetches its own data and renders its own loading, empty and error
	 * state — one slow endpoint costs that panel rather than the screen. On a page of figures that
	 * matters more than usual: an operator reconciling a bad month is the worst possible moment to
	 * hand back a blank page because one aggregate timed out.
	 *
	 * **The aggregate and the rows behind it are the same ledger.** Every panel here is built from
	 * one server-side definition of what a movement of money is, differing only in how it is
	 * grouped — so the summary, the chart, every breakdown and the list at the bottom cannot
	 * disagree. A total nobody can drill into is a figure taken on faith; a list that does not add
	 * up to the total above it is worse than either alone. That is the whole reason the Movements
	 * panel is on this page rather than behind a link.
	 *
	 * Panels are in the order the questions get asked: what did we take, how did it move over time,
	 * where did it come from, who paid it, and finally one movement at a time.
	 */

	import { onMount } from 'svelte';
	import { PageHeader } from '../../components';
	import type { ReportFilterOptions, ReportQuery, ReportSummary } from '../../AppTypes';
	import { getReportFilters, getReportSummary } from '../../AppNetwork';
	import FilterBar from './reports/FilterBar.svelte';
	import SummaryPanel from './reports/SummaryPanel.svelte';
	import TrendPanel from './reports/TrendPanel.svelte';
	import BreakdownPanel from './reports/BreakdownPanel.svelte';
	import LedgerPanel from './reports/LedgerPanel.svelte';
import DocumentsPanel from './reports/DocumentsPanel.svelte';

	/** Thirty days, which is the window somebody means when they do not say. */
	function fnDefaultFilter(): ReportQuery {
		const to = new Date();
		return {
			from: new Date(to.getTime() - 30 * 86400000).toISOString(),
			to: to.toISOString(),
			granularity: 'day'
		};
	}

	let filter: ReportQuery = fnDefaultFilter();
	let options: ReportFilterOptions | null = null;
	let summary: ReportSummary | null = null;
	let summaryError = '';
	let summaryLoading = false;

	/**
	 * Read the filter out of the URL on arrival.
	 *
	 * `kind` is collected rather than assigned, because it is the one repeated parameter — a
	 * `for…of` over `entries()` would otherwise leave the last one standing and silently narrow a
	 * shared link to a single kind of movement.
	 */
	onMount(() => {
		const parameters = new URLSearchParams(window.location.search);
		const fromURL: ReportQuery = { ...fnDefaultFilter() };
		const kinds = parameters.getAll('kind').filter(value => value.length > 0);
		for (const [key, value] of parameters.entries()) {
			if (value.length === 0 || key === 'kind') continue;
			if (key === 'minAmount') {
				const parsed = Number.parseInt(value, 10);
				if (Number.isFinite(parsed)) fromURL.minAmount = parsed;
				continue;
			}
			(fromURL as Record<string, unknown>)[key] = value;
		}
		if (kinds.length > 0) fromURL.kind = kinds as ReportQuery['kind'];
		filter = fromURL;
		void fnLoadOptions();
	});

	async function fnLoadOptions(): Promise<void> {
		try {
			options = await getReportFilters();
		} catch {
			// The bar degrades to its date presets, its kind toggles and its two free-text fields. A
			// filter list that could not be fetched must not cost the page every panel behind it.
			options = null;
		}
	}

	/** Keyed on a string, for the reason the panels' own loads are. */
	$: summaryKey = JSON.stringify(filter);
	$: void fnLoadSummary(summaryKey);

	async function fnLoadSummary(_key: string): Promise<void> {
		summaryLoading = true;
		try {
			summary = await getReportSummary(filter);
			summaryError = '';
		} catch (caught) {
			summaryError = caught instanceof Error ? caught.message : 'Failed to load the summary';
			summary = null;
		} finally {
			summaryLoading = false;
		}
	}

	/**
	 * Reflect the filter into the address bar.
	 *
	 * `history.replaceState` rather than the router's `push`, because changing a filter is not a
	 * navigation: pushing would fill the back button with twelve copies of the same page and make
	 * "back" mean "undo one filter change", which is not what anybody presses it for.
	 */
	function fnFilterChanged(event: CustomEvent<ReportQuery>): void {
		filter = event.detail;
		const parameters = new URLSearchParams();
		for (const [key, value] of Object.entries(filter)) {
			if (value === undefined || value === null || value === '' || value === false) continue;
			if (Array.isArray(value)) {
				for (const entry of value) parameters.append(key, String(entry));
				continue;
			}
			parameters.set(key, String(value));
		}
		window.history.replaceState({}, '', `${window.location.pathname}?${parameters.toString()}`);
	}
</script>

<PageHeader title="Reports">
	<svelte:fragment slot="description">
		Every significant movement of money — sales, renewals, adjustments, refunds and chargebacks —
		filtered, aggregated, and listed one row at a time.
	</svelte:fragment>
	<svelte:fragment slot="help">
		<p class="mb-2">
			The figures and the rows underneath them come from one definition of what a movement is, so
			the totals and the list cannot disagree. Everything is per currency and nothing is summed
			across currencies: minor units in two currencies are different units.
		</p>
		<p class="mb-2">
			A refund and a chargeback are counted apart. One is money given back and the other is money
			taken back, and a refund rate that includes disputes is a rate nobody can act on.
		</p>
		<p>
			The filter is in the address bar, so a slice can be sent to somebody, and both exports carry
			whatever is on screen — the movements as a CSV, and every invoice and credit note the window
			produced as one archive. Documents the payment provider issued live at the provider, so
			those are linked individually rather than included; the archive's manifest says which.
		</p>
	</svelte:fragment>
</PageHeader>

<div class="space-y-6 min-w-0">
	<FilterBar {filter} {options} busy={summaryLoading} on:change={fnFilterChanged} />

	<SummaryPanel {summary} loading={summaryLoading} error={summaryError} />

	<TrendPanel {filter} />

	<BreakdownPanel
		{filter}
		title="What sold"
		faIcon="box-open"
		help="Which products and plans the money came through. A product sold in two currencies is two rows — one row with them merged would be a figure that is not money in either."
		dimensions={[
			{ value: 'product', name: 'Product' },
			{ value: 'family', name: 'Product family' },
			{ value: 'interval', name: 'Billing period' },
			{ value: 'billing', name: 'Subscription or one-off' },
			{ value: 'coupon', name: 'Coupon' }
		]}
	/>

	<BreakdownPanel
		{filter}
		title="Who paid"
		faIcon="users"
		help="A customer's row is everything they have ever moved in this window, refunds included — so somebody who bought and returned nets to nothing rather than disappearing."
		dimensions={[
			{ value: 'customer', name: 'Customer' },
			{ value: 'country', name: 'Country' },
			{ value: 'currency', name: 'Currency' }
		]}
	/>

	<BreakdownPanel
		{filter}
		title="How it was booked"
		faIcon="building-columns"
		help="The legal entity that issued the document and the provider that moved the money. A store with more than one seller files a return for each."
		dimensions={[
			{ value: 'kind', name: 'Kind of movement' },
			{ value: 'seller', name: 'Seller' },
			{ value: 'provider', name: 'Payment provider' }
		]}
	/>

	<LedgerPanel {filter} />

	<!--
		Last, because it is what somebody leaves with rather than what they arrive for. It is also
		the one panel on this page that did not exist in any form before: an invoice was reachable
		only from the customer or the sale it belonged to, so "give me this quarter's invoices" meant
		visiting several hundred pages one at a time.
	-->
	<DocumentsPanel {filter} />
</div>
